import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { ArrowLeftRight } from "lucide-react";
import {
  clampHeightCm,
  cmToFeet,
  feetToCm,
  formatHeight,
  getHeightRangeDescription,
  getHeightSliderBounds,
  HEIGHT_GLOBAL_MAX_CM,
  HEIGHT_GLOBAL_MIN_CM,
} from "./heightUtils";
import { useHeightDisplayUnit } from "./useHeightDisplayUnit";
import { RangeSlider } from "./RangeSlider";
import { useIsMobile } from "./useIsMobile";

export function HeightRangePicker({
  minHeight,
  maxHeight,
  userHeight,
  preferMobileControls = false,
  onChangeMin,
  onChangeMax,
}: {
  minHeight?: number;
  maxHeight?: number;
  userHeight?: number;
  preferMobileControls?: boolean;
  onChangeMin: (value: number) => void;
  onChangeMax: (value: number) => void;
}) {
  const mobile = useIsMobile();
  const useMobileControls = mobile || preferMobileControls;
  const { useImperial, setUseImperial } = useHeightDisplayUnit();
  const defaultMin = userHeight ? Math.max(HEIGHT_GLOBAL_MIN_CM, userHeight - 15) : 150;
  const defaultMax = userHeight ? Math.min(HEIGHT_GLOBAL_MAX_CM, userHeight + 15) : 200;
  const propsMinHeight = minHeight ?? defaultMin;
  const propsMaxHeight = maxHeight ?? defaultMax;

  const [displayRange, setDisplayRange] = useState({ min: propsMinHeight, max: propsMaxHeight });
  const [editing, setEditing] = useState<"min" | "max" | null>(null);
  const [draftCm, setDraftCm] = useState("");
  const [draftFeet, setDraftFeet] = useState("");
  const [draftInches, setDraftInches] = useState("");

  const minInputRef = useRef<HTMLInputElement>(null);
  const maxInputRef = useRef<HTMLInputElement>(null);
  const minFeetRef = useRef<HTMLInputElement>(null);
  const maxFeetRef = useRef<HTMLInputElement>(null);
  const minInchesRef = useRef<HTMLInputElement>(null);
  const maxInchesRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDisplayRange({ min: propsMinHeight, max: propsMaxHeight });
  }, [propsMinHeight, propsMaxHeight]);

  const description = useMemo(
    () => getHeightRangeDescription(displayRange.min, displayRange.max, userHeight, useImperial),
    [displayRange.max, displayRange.min, userHeight, useImperial],
  );

  const { tightMin, tightMax, renderMin, renderMax } = useMemo(
    () => getHeightSliderBounds(userHeight, displayRange.min, displayRange.max),
    [displayRange.max, displayRange.min, userHeight],
  );

  const applyRangeFromSlider = (low: number, high: number) => {
    const minValue = clampHeightCm(Math.min(low, high));
    const maxValue = clampHeightCm(Math.max(low, high));
    const clampedLow = Math.max(tightMin, Math.min(tightMax, minValue));
    const clampedHigh = Math.max(tightMin, Math.min(tightMax, maxValue));
    const lowFinal = Math.min(clampedLow, clampedHigh);
    const highFinal = Math.max(clampedLow, clampedHigh);
    setDisplayRange({ min: lowFinal, max: highFinal });
    onChangeMin(lowFinal);
    onChangeMax(highFinal);
  };

  const applyRangeFromInput = (low: number, high: number) => {
    const minValue = clampHeightCm(Math.min(low, high));
    const maxValue = clampHeightCm(Math.max(low, high));
    setDisplayRange({ min: minValue, max: maxValue });
    onChangeMin(minValue);
    onChangeMax(maxValue);
  };

  const focusInput = (field: "min" | "max") => {
    if (useImperial) {
      const input = (field === "min" ? minFeetRef : maxFeetRef).current;
      input?.focus();
      input?.select();
      return;
    }

    const input = (field === "min" ? minInputRef : maxInputRef).current;
    input?.focus();
    input?.select();
  };

  const beginEdit = (field: "min" | "max", immediateFocus = false) => {
    const cm = field === "min" ? displayRange.min : displayRange.max;
    const imperial = cmToFeet(cm);
    const applyEditState = () => {
      setEditing(field);
      setDraftCm(String(cm));
      setDraftFeet(String(imperial.feet));
      setDraftInches(String(imperial.inches));
    };

    if (immediateFocus) {
      flushSync(applyEditState);
      focusInput(field);
      return;
    }

    applyEditState();
    requestAnimationFrame(() => {
      focusInput(field);
    });
  };

  const commitEdit = (field: "min" | "max") => {
    let cm: number | null = null;

    if (useImperial) {
      const feet = Number.parseInt(draftFeet, 10);
      const inches = Number.parseInt(draftInches, 10);
      if (!Number.isNaN(feet) && !Number.isNaN(inches)) {
        const clampedInches = Math.max(0, Math.min(11, inches));
        cm = feetToCm(Math.max(4, Math.min(8, feet)), clampedInches);
      }
    } else {
      const parsed = Number.parseInt(draftCm, 10);
      if (!Number.isNaN(parsed)) cm = parsed;
    }

    if (cm !== null) {
      if (field === "min") applyRangeFromInput(cm, displayRange.max);
      else applyRangeFromInput(displayRange.min, cm);
    }

    setEditing(null);
    setDraftCm("");
    setDraftFeet("");
    setDraftInches("");
  };

  const toggleUnit = () => {
    if (editing) commitEdit(editing);
    setUseImperial(!useImperial);
  };

  if (!useMobileControls) {
    return (
      <div className="age-range-picker age-range-picker--desktop">
        <div className="inline-fields preferences-inline-fields">
          <label className="field">
            <span>Min height</span>
            <input
              type="number"
              min={HEIGHT_GLOBAL_MIN_CM}
              max={HEIGHT_GLOBAL_MAX_CM}
              value={minHeight || ""}
              onChange={(event) => onChangeMin(event.target.value ? Number(event.target.value) : defaultMin)}
            />
          </label>
          <label className="field">
            <span>Max height</span>
            <input
              type="number"
              min={HEIGHT_GLOBAL_MIN_CM}
              max={HEIGHT_GLOBAL_MAX_CM}
              value={maxHeight || ""}
              onChange={(event) => onChangeMax(event.target.value ? Number(event.target.value) : defaultMax)}
            />
          </label>
        </div>
        <p className="age-range-picker-note">{description}</p>
      </div>
    );
  }

  const renderHeightBox = (field: "min" | "max") => {
    const cm = field === "min" ? displayRange.min : displayRange.max;
    const label = field === "min" ? "Min height" : "Max height";
    const cmInputRef = field === "min" ? minInputRef : maxInputRef;
    const feetRef = field === "min" ? minFeetRef : maxFeetRef;
    const inchesRef = field === "min" ? minInchesRef : maxInchesRef;

    return (
      <div
        className={`age-range-box ${editing === field ? "active" : ""}`}
        onPointerDown={(event) => {
          if (editing !== field) {
            if (editing) commitEdit(editing);
            event.preventDefault();
            beginEdit(field, true);
          }
        }}
        onClick={() => {
          if (editing !== field) beginEdit(field);
        }}
      >
        <span className="age-range-box-label">{label}</span>
        {editing === field && useImperial ? (
          <div
            className="height-box-imperial-inputs"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                commitEdit(field);
              }
            }}
          >
            <input
              ref={feetRef}
              className="age-range-box-input height-box-feet"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              enterKeyHint="next"
              aria-label={`${label} feet`}
              maxLength={1}
              value={draftFeet}
              onFocus={() => {
                if (editing !== field) beginEdit(field);
              }}
              onChange={(event) => setDraftFeet(event.target.value.replace(/\D/g, "").slice(0, 1))}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  inchesRef.current?.focus();
                  inchesRef.current?.select();
                }
              }}
            />
            <span className="height-box-unit-mark" aria-hidden="true">'</span>
            <input
              ref={inchesRef}
              className="age-range-box-input height-box-inches"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              enterKeyHint="done"
              aria-label={`${label} inches`}
              maxLength={2}
              value={draftInches}
              onChange={(event) => setDraftInches(event.target.value.replace(/\D/g, "").slice(0, 2))}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  event.currentTarget.blur();
                }
              }}
            />
            <span className="height-box-unit-mark" aria-hidden="true">"</span>
          </div>
        ) : editing === field ? (
          <div className="height-box-metric-input">
            <input
              ref={cmInputRef}
              className="age-range-box-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              enterKeyHint="done"
              aria-label={label}
              maxLength={3}
              value={draftCm}
              onFocus={() => {
                if (editing !== field) beginEdit(field);
              }}
              onChange={(event) => setDraftCm(event.target.value.replace(/\D/g, "").slice(0, 3))}
              onBlur={() => commitEdit(field)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  cmInputRef.current?.blur();
                }
              }}
            />
            <span className="height-box-unit-mark" aria-hidden="true">cm</span>
          </div>
        ) : (
          <span className="age-range-box-value">{formatHeight(cm, useImperial)}</span>
        )}
      </div>
    );
  };

  return (
    <div className="age-range-picker height-range-picker">
      <div className="height-range-picker-toolbar">
        <span className="height-range-unit-label">{useImperial ? "Feet & inches" : "Centimeters"}</span>
        <button className="height-range-unit-toggle" type="button" onClick={toggleUnit}>
          {useImperial ? "Switch to cm" : "Switch to ft/in"}
          <ArrowLeftRight size={16} aria-hidden="true" />
        </button>
      </div>

      <div className="age-range-display">
        {renderHeightBox("min")}
        {renderHeightBox("max")}
      </div>

      <RangeSlider
        min={renderMin}
        max={renderMax}
        low={displayRange.min}
        high={displayRange.max}
        onChange={applyRangeFromSlider}
        ariaLabel="Preferred height range"
      />

      <p className="age-range-picker-note">{description}</p>
    </div>
  );
}
